import { DEFAULT_RECORDER_HOST, DEFAULT_RECORDER_PORTS, RECORDER_KINDS } from 'modules/video-markers/domain/constants';
import { RecorderConfig, RecorderKind } from 'modules/video-markers/domain/interfaces';

/**
 * Настройка подключения едет в оверлей `#`-фрагментом ссылки на табло, а не query: фрагмент
 * браузер на сервер не отправляет, и пароль OBS не попадает ни в логи nginx, ни на бэкенд.
 *
 * Возвращает фрагмент **без** `#`: `recorder=obs&host=127.0.0.1&port=4455&password=…`.
 * Значения кодирует `URLSearchParams`, так что `&`, `#` и `=` в пароле не ломают разбор.
 */
export function recorderConfigToFragment(config: RecorderConfig): string {
  const params = new URLSearchParams({
    recorder: config.recorder,
    host: config.host,
    port: String(config.port),
  });

  if (config.recorder === 'obs' && config.password) params.set('password', config.password);

  return params.toString();
}

/**
 * Обратная сторона: `location.hash` оверлея → настройка. Нет фрагмента или в нём неизвестная
 * программа — `null`, и оверлей ведёт себя как без видеометок. Пустой хост и неверный порт
 * заменяются значениями по умолчанию: ссылку собирают руками в OBS, и опечатка в порту не должна
 * выключать метки целиком.
 */
export function parseRecorderFragment(hash: string): RecorderConfig | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));

  const recorder = params.get('recorder');
  if (!isRecorderKind(recorder)) return null;

  const host = params.get('host')?.trim() || DEFAULT_RECORDER_HOST;
  const port = parsePort(params.get('port')) ?? DEFAULT_RECORDER_PORTS[recorder];
  const password = recorder === 'obs' ? params.get('password') || undefined : undefined;

  return password === undefined ? { recorder, host, port } : { recorder, host, port, password };
}

function isRecorderKind(value: string | null): value is RecorderKind {
  return RECORDER_KINDS.includes(value as RecorderKind);
}

function parsePort(value: string | null): number | null {
  if (!value || !/^\d+$/.test(value)) return null;

  const port = Number(value);
  return port >= 1 && port <= 65535 ? port : null;
}
